import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import API from "@/lib/api";
import {
  Copy, Check, Download, Loader2, ArrowLeft, FileText,
  Target, Pencil, Wrench, ClipboardCheck, Gauge
} from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [
  { value: "emergent", label: "Emergent" },
  { value: "vibe-code", label: "Vibe-Code Platforms" },
  { value: "ai-assistants", label: "AI Coding Assistants (Cursor/Copilot)" },
  { value: "no-code", label: "No-Code Builders" },
  { value: "product-planning", label: "Product Planning" },
  { value: "general", label: "General" },
];

const MODES = [
  { id: "define",    label: "Define",    icon: Target },
  { id: "shape",     label: "Shape",     icon: Pencil },
  { id: "implement", label: "Implement", icon: Wrench },
  { id: "verify",    label: "Verify",    icon: ClipboardCheck },
  { id: "iterate",   label: "Iterate",   icon: Gauge },
];

export function ExportStep({ promptData, onBack }) {
  const [platform, setPlatform] = useState(promptData?.target_platform || "general");
  const [selectedModes, setSelectedModes] = useState(MODES.map((m) => m.id));
  const [includeMaster, setIncludeMaster] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [formattedOutput, setFormattedOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const toggleMode = (mode) => {
    setSelectedModes((prev) =>
      prev.includes(mode) ? prev.filter((m) => m !== mode) : [...prev, mode]
    );
  };

  const handleExport = async () => {
    if (!promptData?.id) return;
    setExporting(true);
    setFormattedOutput("");
    try {
      const { data } = await API.post(`/prompts/${promptData.id}/export`, {
        prompt_id: promptData.id,
        target_platform: platform,
        include_master: includeMaster,
        selected_modes: selectedModes,
      });
      setFormattedOutput(data.formatted_output || "");
      toast.success("Export ready", { description: `${data.block_count} blocks formatted for ${platform}` });
    } catch (e) {
      toast.error("Export failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setExporting(false);
    }
  };

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(formattedOutput); } catch {
      const ta = document.createElement("textarea"); ta.value = formattedOutput; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard");
    recordExportEvent();
  };

  const handleDownload = () => {
    const blob = new Blob([formattedOutput], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `staqblox-export-${platform}-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
    recordExportEvent();
  };

  // Reflection loop (P1.1): record that the user took the playbook out to an
  // external builder. We'll ask "did it work?" on their next visit. Fire-and-
  // forget — never block the copy/download UX on this. Once-per-export.
  const exportRecordedRef = useRef(false);
  const recordExportEvent = () => {
    if (exportRecordedRef.current) return;
    exportRecordedRef.current = true;
    const projectId = promptData?.project_id;
    if (!projectId) return;
    API.post("/exports", {
      project_id: projectId,
      playbook_id: promptData?.id,
      platform,
      selected_modes: selectedModes,
    }).catch(() => { exportRecordedRef.current = false; }); // allow retry on next click if it failed
  };

  const blockCount = MODES.reduce((sum, m) => sum + (promptData?.blocks?.[m.id]?.length || 0), 0);
  const evalScore = promptData?.evaluation?.score;
  const evalGrade = promptData?.evaluation?.grade;
  const isLowQuality = evalScore !== undefined && evalScore < 70;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="export-step">
      {/* Quality Warning */}
      {isLowQuality && (
        <div className="border-2 border-[#EAB308] bg-yellow-50 dark:bg-yellow-900/10 px-5 py-3 flex items-start gap-3" data-testid="export-quality-warning">
          <span className="text-lg mt-0.5">!</span>
          <div>
            <p className="text-sm font-medium text-[var(--pf-text)]">Quality Score: {evalScore}/100 ({evalGrade})</p>
            <p className="text-xs text-[var(--pf-text-secondary)] mt-0.5">This prompt scored below 70. Consider going back to the Evaluate step to review and address the suggestions before exporting.</p>
          </div>
        </div>
      )}
      {evalScore !== undefined && !isLowQuality && (
        <div className="border border-[#16A34A] bg-green-50 dark:bg-green-900/10 px-5 py-2 flex items-center gap-2" data-testid="export-quality-badge">
          <span className="text-xs font-medium text-[#16A34A]">Quality Score: {evalScore}/100 ({evalGrade})</span>
        </div>
      )}

      {/* Export Configuration */}
      <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)]">
        <div className="px-5 py-3 border-b border-[var(--pf-border)]">
          <h3 className="font-heading text-base font-bold text-[var(--pf-text)]">Export Configuration</h3>
          <p className="text-xs text-[var(--pf-text-secondary)] mt-0.5">Format your prompt for a specific platform. The output will be optimized for how that platform processes instructions.</p>
        </div>
        <div className="p-5 space-y-4">
          {/* Platform selector */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-2">Target Platform</p>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="rounded-none border-[var(--pf-border)] h-9 text-sm w-64" data-testid="export-platform-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none border-[var(--pf-border)]">
                {PLATFORMS.map((p) => (
                  <SelectItem key={p.value} value={p.value} className="rounded-none text-sm">{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Include Master */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeMaster}
              onChange={(e) => setIncludeMaster(e.target.checked)}
              data-testid="export-include-master"
              className="w-3.5 h-3.5 rounded-none border-[var(--pf-border)] accent-[var(--pf-accent)]"
            />
            <span className="text-xs text-[var(--pf-text)]">Include master prompt</span>
          </div>

          {/* Mode selection */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-2">Include Blocks From</p>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="export-mode-selection">
              {MODES.map((m) => {
                const isSelected = selectedModes.includes(m.id);
                const count = (promptData?.blocks?.[m.id] || []).length;
                const Icon = m.icon;
                return (
                  <button
                    key={m.id}
                    onClick={() => toggleMode(m.id)}
                    data-testid={`export-mode-${m.id}`}
                    className={`text-[11px] px-2.5 py-1.5 font-medium transition-colors flex items-center gap-1.5 ${
                      isSelected
                        ? "bg-[var(--pf-accent)] text-white"
                        : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    {m.label} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Export button */}
          <Button
            onClick={handleExport}
            disabled={exporting || (selectedModes.length === 0 && !includeMaster)}
            data-testid="export-generate-button"
            className="bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-10 px-5 text-sm font-medium"
          >
            {exporting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Formatting...</> : <><FileText className="w-4 h-4 mr-1.5" />Generate Export</>}
          </Button>
        </div>
      </div>

      {/* Formatted Output */}
      {formattedOutput && (
        <div className="border border-[var(--pf-accent)] border-2 bg-[var(--pf-bg)]" data-testid="export-output">
          <div className="px-5 py-3 border-b border-[var(--pf-accent)] bg-[var(--pf-accent-subtle)] flex items-center justify-between">
            <div>
              <h3 className="font-heading text-base font-bold text-[var(--pf-accent)]">Ready to Use</h3>
              <p className="text-xs text-[var(--pf-text-secondary)] mt-0.5">Formatted for {PLATFORMS.find((p) => p.value === platform)?.label || platform}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline" size="sm"
                onClick={handleCopy}
                data-testid="export-copy-button"
                className="rounded-none border-[var(--pf-accent)] h-8 px-3 text-xs text-[var(--pf-accent)] hover:bg-[var(--pf-accent)] hover:text-white"
              >
                {copied ? <><Check className="w-3 h-3 mr-1" />Copied</> : <><Copy className="w-3 h-3 mr-1" />Copy</>}
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={handleDownload}
                data-testid="export-download-button"
                className="rounded-none border-[var(--pf-accent)] h-8 px-3 text-xs text-[var(--pf-accent)] hover:bg-[var(--pf-accent)] hover:text-white"
              >
                <Download className="w-3 h-3 mr-1" />Download .md
              </Button>
            </div>
          </div>
          <div className="p-5">
            <div className="bg-[var(--pf-surface)] border border-[var(--pf-border)] p-4 font-mono text-sm leading-relaxed text-[var(--pf-text)] whitespace-pre-wrap max-h-[500px] overflow-y-auto" data-testid="export-formatted-content">
              {formattedOutput}
            </div>
          </div>
        </div>
      )}

      {/* Back button */}
      <div className="flex items-center pt-2">
        <Button variant="outline" onClick={onBack} data-testid="export-back" className="rounded-none border-[var(--pf-border)] h-9 px-4 text-xs">
          <ArrowLeft className="w-3 h-3 mr-1" />Back to Blocks
        </Button>
      </div>
    </div>
  );
}
